import { cn } from "@/lib/utils";
import { useMatchRoute, useNavigate } from "@tanstack/react-router";
import { bucketUrlPrefix } from "common/config";
import { getVenueLocationSubtitle } from "common/utils";
import {
  VenueDetailsBySlugResponse,
  useAuth,
  useFilters,
  useI18N,
  useSavedPlaces,
  useSyncBusinessInterest,
} from "common/hooks";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";
import Modal from "react-responsive-modal";
import { useCallback, useEffect, useState, useMemo, useRef } from "react";
import { BusinessPhoto } from "common/types";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { useAtom } from "jotai";
import {
  actionToPerformAfterLoginAtom,
  actionToPerformValueAfterLoginAtom,
} from "@/store/atoms";
import { ArrowLeftIcon, CaretRightIcon, WarningIcon, PlusCircleIcon, FolderDottedIcon } from "@phosphor-icons/react";
import AnalyticsService from "@/services/analytics";
import { useDarkMode } from "@/hooks/useDarkMode";
import CreateList from "@/components/SavedPlaces/CreateList";
import AddRemoveVenueToList from "@/components/SavedPlaces/AddRemoveVenueToList";
import VenueImageWithActions from "./VenueImageWithActions";
import OpeningHours from "@/components/VenueCard/TodaysOpeningHours";
import AccessibilitySection from "./AccessibilitySection";
import PhotosTab from "./PhotosTab";
import AlertsTab from "./AlertsTab";
import ReviewsTab from "./ReviewsTab";
import AboutTab from "./AboutTab";
import FlagVenueReview from "@/features/flag-report/FlagVenueReview";
import EditVenueNote from "@/components/VenueDetailBasic/Feature/EditVenueNote";
import FlagVenueNote from "@/features/flag-report/FlagVenueNote";
import AddMapperNote from "@/components/VenueDetailBasic/Feature/AddMapperNote";
import AreaDetailsNotes from "@/components/VenueDetailBasic/Feature/AreaDetailsNotes";
import { AddReviewModal } from "@/features/add-review/AddReviewModal";
import VenueInfoClaim from "@/components/VenueDetailBasic/Feature/VenueInfoClaim";
import ScrollContainer from "react-indiana-drag-scroll";
import { RowsPhotoAlbum } from "react-photo-album";
import FullImageViewer from "@/components/FullImageViewer";

import Loader from "@/components/common/Loader";
import { InterestNotificationModal } from "../Notifications/NotificationModal";

type VenueSheetContentProps = {
  onBack: () => void;
  venueDetailsResponse: VenueDetailsBySlugResponse;
  fullSize?: boolean;
  onRefresh: () => void;
  openAllPhotosInline: boolean;
  onOpenAllPhotos?: () => void;
  cid?: string;
  tid?: string;
  currentVenueTypePage?: number;
  onShowDetailView?: (initialSpaceId: string) => void;
  onShowPhotosDetailView?: (initialCategoryId: number | null) => void;
  tb?: string;
  tab?: string;
  openModal?: boolean;
  source?: "map" | "activity" | "notifications-page" | "notification-settings";
};

function getFivePhotos(businessPhotos: BusinessPhoto[]): BusinessPhoto[] {
  // Separate photos by user role priority
  const priorityPhotos: BusinessPhoto[] = []; // isGeneralUser: false
  const generalPhotos: BusinessPhoto[] = []; // isGeneralUser: true

  // Categorize photos by user role
  for (const photo of businessPhotos) {
    if (photo.user_roles?.isGeneralUser === false) {
      priorityPhotos.push(photo);
    } else {
      generalPhotos.push(photo);
    }
  }

  const uniqueTagPhotosMap = new Map<string, BusinessPhoto>();
  const result: BusinessPhoto[] = [];

  // First, populate with priority photos (isGeneralUser: false) ensuring unique tagNames
  for (const photo of priorityPhotos) {
    if (!uniqueTagPhotosMap.has(photo.tagName)) {
      uniqueTagPhotosMap.set(photo.tagName, photo);
    }
    if (uniqueTagPhotosMap.size >= 5) {
      break;
    }
  }

  // If we still need more photos, add from general photos (isGeneralUser: true)
  if (uniqueTagPhotosMap.size < 5) {
    for (const photo of generalPhotos) {
      if (!uniqueTagPhotosMap.has(photo.tagName)) {
        uniqueTagPhotosMap.set(photo.tagName, photo);
      }
      if (uniqueTagPhotosMap.size >= 5) {
        break;
      }
    }
  }

  // Add unique photos to the result
  const uniquePhotos = Array.from(uniqueTagPhotosMap.values());
  result.push(...uniquePhotos);

  // If we have fewer than 5 unique photos, add more photos without duplicating
  // First try priority photos, then general photos
  const remainingPhotos = [...priorityPhotos, ...generalPhotos];

  for (const photo of remainingPhotos) {
    if (result.length >= 5) {
      break;
    }
    if (!result.includes(photo)) {
      result.push(photo);
    }
  }

  return result;
}

// VenueSummary component with expand/collapse functionality
function VenueSummary({ summary, onExpand }: { summary: string; onExpand?: (isExpanded: boolean) => void }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const i18n = useI18N();

  const handleToggle = () => {
    const newState = !isExpanded;
    setIsExpanded(newState);
    if (onExpand) {
      onExpand(newState);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <p
        className={cn(
          "label-m-medium text-fg-neutral-strong min-h-[2rem]",
          !isExpanded && "line-clamp-2"
        )}
      >
        {summary}
      </p>
      <button
        onClick={handleToggle}
        aria-expanded={isExpanded}
        className="flex items-center gap-1 self-start 
      "
      >
        <span className="label-m-semibold
        text-fg-brand-subtle
        hover:text-fg-brand-strong
        hover:border-b hover:border-border-token-brand-subtle 
        active:border-b active:border-border-token-brand-strong 
        active:text-fg-brand-strong
        transition-all duration-200 focus-blue">
          {isExpanded ? i18n.get("viewLess") : i18n.get("viewMore")}
        </span>
        <CaretRightIcon
          size={16}
          weight="bold"
          aria-hidden="true"
          className={cn(
            "transition-transform",
            isExpanded ? "-rotate-90" : "rotate-90"
          )}
        />
      </button>
    </div>
  );
}

function VenueSheetContent({
  onBack,
  venueDetailsResponse,
  fullSize = false,
  onRefresh,
  openAllPhotosInline,
  cid,
  tid,
  currentVenueTypePage,
  onShowDetailView,
  onShowPhotosDetailView,
  tb,
  tab,
  openModal,
  source,
}: VenueSheetContentProps) {
  const i18n = useI18N();

  // Debug: log the source prop
  // console.log("VenueSheetContent - source prop:", source);
  const venueDetails = venueDetailsResponse.details;
  const accessibilityCategories = venueDetailsResponse.accesibilityCategories;

  const locationOnly = getVenueLocationSubtitle({
    sublocality: venueDetails.sublocality,
    route: venueDetails.route,
    postalTown: venueDetails.postalTown,
    locality: venueDetails.locality,
    cityLocation: venueDetails.cityLocation,
  });

  const navigate = useNavigate();
  const matchRoute = useMatchRoute();

  const [currentReviewSort, setCurrentReviewSort] = useState("Latest");
  const [reviews, setReviews] = useState(venueDetails.businessReviews);
  const [areaDetailsOpenModal, setAreaDetailsOpenModal] = useState(false);

  // Update reviews when venueDetails changes (e.g., after refetch from adding a review)
  useEffect(() => {
    setReviews(venueDetails.businessReviews);
  }, [venueDetails.businessReviews]);

  const { showModal } = useAuthModals();

  const { isLoggedIn, user } = useAuth();
  const showMapperView =
    isLoggedIn &&
    (user?.roles?.isSociabilityMapper || user?.roles?.isSociabilityTeam);

  const [isFlagReviewOpen, setIsFlagReviewOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<any>(null);
  const [addNoteOpenModal, setAddNoteOpenModal] = useState(false);

  const [editVenueNoteOpenModal, setEditVenueNoteOpenModal] = useState(false);
  const [editNote, setEditNote] = useState(null);
  const [flagVenueNoteOpenModal, setFlagVenueNoteOpenModal] = useState(false);
  const [flagNote, setFlagNote] = useState(null);
  const [isAddReviewOpen, setIsAddReviewOpen] = useState(false);
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [preSelectedNotificationType, setPreSelectedNotificationType] = useState<'accessibility' | 'liveAlerts' | null>(null);
  
  const { isLoading: isSyncingInterest } = useSyncBusinessInterest();

  // Generate accessibility alerts from businessNotesAll
  const accessibilityAlerts = useMemo(() => {
    return (
      venueDetails.businessNotesAll
        ?.filter((note) => note.status === "Open")
        .map((note) => ({
          id: note.business_note_text_id,
          dateRange:
            note.formatedScheduleDateFrom && note.formatedScheduleDateTo
              ? `${note.formatedScheduleDateFrom} to ${note.formatedScheduleDateTo}`
              : "",
          flaggedDate: `Added on ${note.formatedCreatedAt} by ${note.business_note_by_user}`,
          title: note.business_note_title,
          location: note.location_name,
          description: note.business_note_text,
        })) || []
    );
  }, [venueDetails.businessNotesAll]);

  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } =
    useNearbyBusinesses(venueDetails.business.id.toString());

  const [fullImageView, setFullImageView] = useState(false);
  const [currentImage, setCurrentImage] = useState<string | null>(null);
  const [photoDimensions, _setPhotoDimensions] = useState<{
    [key: string]: { width: number; height: number };
  }>({});

  const [sortByText, setSortByText] = useState("Date added (Newest)");

  const [_showInlinePhotos] = useState(false);

  // Add hasNearbyVenues constant
  const hasNearbyVenues = useMemo(() => {
    if (isLoadingNearbyBusinesses) return false;
    return (
      venueDetails.templateType !== "Basic" &&
      nearbyBusinessesData?.data?.nearbyBusinessCategories?.length > 0
    );
  }, [
    isLoadingNearbyBusinesses,
    venueDetails.templateType,
    nearbyBusinessesData,
  ]);

  const hasAccessibilityData =
    !hasNearbyVenues || accessibilityCategories?.length > 0;

  const isLoadingTabs = isLoadingNearbyBusinesses;

  const [showReviewsTab] = useState(true);
  const [showNotesTab] = useState(true);

  // Initial render uses the default tab — sessionStorage is read in the
  // useEffect below so the initial value is identical on server and client
  // (avoids SSR ReferenceError and hydration mismatch).
  const [selectedMainTab, setSelectedMainTab] = useState<
    "accessibility" | "photos" | "alerts" | "reviews" | string
  >("accessibility");

  // Ref to track if modal was already auto-opened from query param
  const hasAutoOpenedModalRef = useRef(false);

  // Refs for tab buttons to enable keyboard navigation
  const desktopTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const mobileTabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabContentScrollRef = useRef<HTMLDivElement>(null);

  // Store tab selection in sessionStorage whenever it changes
  useEffect(() => {
    if (venueDetails?.id && selectedMainTab) {
      sessionStorage.setItem(
        `selectedMainTab_${venueDetails.id}`,
        selectedMainTab,
      );
    }

    // Cleanup: Remove stored state when component unmounts
    return () => {
      // Note: We intentionally keep the state in sessionStorage even on unmount
      // so it persists during page refresh. It will be cleared when browser tab closes.
    };
  }, [selectedMainTab, venueDetails?.id]);

  // Handle back navigation with tab reset
  const handleBack = useCallback(() => {
    // Clear the stored tab for this venue so it resets to accessibility on next visit
    if (venueDetails?.id) {
      sessionStorage.removeItem(`selectedMainTab_${venueDetails.id}`);
    }
    onBack();
  }, [venueDetails?.id, onBack]);

  useEffect(() => {
    switch (currentReviewSort) {
      case "Latest":
        setSortByText("Date added (Newest)");
        break;
      case "Earliest":
        setSortByText("Date added (Oldest)");
        break;
      case "Alphabetical":
        setSortByText("Alphabetical");
        break;
      default:
        setSortByText("Date added (Newest)");
    }
  }, [currentReviewSort]);

  // Update selected tab when loading completes (but respect stored tab if it exists)
  useEffect(() => {
    if (!isLoadingTabs) {
      const storedTab = sessionStorage.getItem(
        `selectedMainTab_${venueDetails?.id}`,
      );

      // If tab param is explicitly passed, override stored tab preference
      if (tab === "reviews") {
        setSelectedMainTab("reviews");
      } else if (tab) {
        // Handle other tab values if needed in the future
        setSelectedMainTab(tab);
      } else if (storedTab) {
        setSelectedMainTab(storedTab);
      } else {
        // Only set default tab if no stored tab exists and no tab param
        // If tb=photo is passed, default to photos tab
        if (tb === "photo" && venueDetails?.businessPhotos?.length > 0) {
          setSelectedMainTab("photos");
        } else if (hasAccessibilityData) {
          setSelectedMainTab("accessibility");
        } else if (venueDetails?.businessPhotos?.length > 0) {
          setSelectedMainTab("photos");
        } else if (venueDetails?.businessNotesAll?.length > 0) {
          setSelectedMainTab("alerts");
        } else {
          setSelectedMainTab("reviews");
        }
      }
    }
  }, [isLoadingTabs, hasAccessibilityData, venueDetails, tb, tab]);

  // Auto-open AddReviewModal when openModal param is true and reviews tab is active
  useEffect(() => {
    if (
      openModal &&
      !isLoadingTabs &&
      selectedMainTab === "reviews" &&
      venueDetails &&
      !hasAutoOpenedModalRef.current
    ) {
      if (isLoggedIn) {
        setIsAddReviewOpen(true);
      } else {
        handleShowLoginModal();
      }
      
      // Mark that we've auto-opened the modal
      hasAutoOpenedModalRef.current = true;
      
      // Clear the openModal query param to prevent reopening after submission
      // Preserve all other query params
      navigate({
        to: `/venuedetails/${venueDetails.business.slug}`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: {
          ...(source && { source }),
          ...(cid && { cid }),
          ...(tid && { tid }),
          ...(tb && { tb }),
          ...(tab && { tab }),
        } as any,
        replace: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModal, isLoadingTabs, selectedMainTab, venueDetails, isLoggedIn]);

  // Determine which tabs to show
  const hasPhotos = venueDetails?.businessPhotos?.length > 0;
  const hasAlerts = venueDetails?.businessNotesAll?.length > 0;
  const hasReviews = venueDetails?.businessReviews?.length > 0;

  type TabItem = { id: string; label: string; badgeCount: number };

  // CheckIcon for dynamic tabs from API response
  const dynamicTabs = useMemo(() => {
    return venueDetails.businessTabs || [];
  }, [venueDetails.businessTabs]);

  // Build tabs array dynamically
  const mainTabItems: TabItem[] = [];

  if (hasAccessibilityData) {
    mainTabItems.push({ id: "accessibility", label: i18n.get("accessibility"), badgeCount: 0 });
  }
  if (hasAlerts || showNotesTab) {
    mainTabItems.push({ id: "alerts", label: i18n.get("alerts"), badgeCount: venueDetails?.businessNotesAll?.length ?? 0 });
  }
  if (hasPhotos) {
    mainTabItems.push({ id: "photos", label: i18n.get("photos"), badgeCount: venueDetails?.businessPhotos?.filter((p) => p.photoType !== "Google Maps").length ?? 0 });
  }
  if (hasReviews || showReviewsTab) {
    mainTabItems.push({ id: "reviews", label: i18n.get("experiences"), badgeCount: venueDetails?.businessReviews?.length ?? 0 });
  }

  mainTabItems.push({ id: "about", label: i18n.get("about"), badgeCount: 0 });

  if (dynamicTabs.length > 0) {
    dynamicTabs.forEach((tab) => {
      mainTabItems.push({ id: `dynamicTab_${tab.id}`, label: tab.title, badgeCount: 0 });
    });
  }

  const mainTabs = mainTabItems.map((t) => t.id);

  const [_initialSection, setInitialSection] = useState("All");
  const [hasProcessedCidForTabSwitch, setHasProcessedCidForTabSwitch] =
    useState(false);

  // Update initial section when cid or accessibilityCategories change
  useEffect(() => {
    if (cid && accessibilityCategories && accessibilityCategories.length > 0) {
      const category = accessibilityCategories.find(
        (cat) => cat.catId === parseInt(cid),
      );
      if (category) {
        setInitialSection(category.catName);

        // Auto-switch to accessibility tab and scroll to it (only once)
        if (hasAccessibilityData && !hasProcessedCidForTabSwitch) {
          setSelectedMainTab("accessibility");
          setHasProcessedCidForTabSwitch(true);

          // Scroll to accessibility section after tab switch
          setTimeout(() => {
            const accessibilitySection = document.querySelector(
              '[data-section="accessibility"]',
            );
            console.log(
              "Looking for accessibility section:",
              accessibilitySection,
            );
            if (accessibilitySection) {
              console.log("Found accessibility section, scrolling to it");
              accessibilitySection.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            } else {
              console.log(
                "Accessibility section not found, trying alternative approach",
              );
              // Try scrolling to the main content area
              const mainContent =
                document.querySelector(".content-area") ||
                document.querySelector("main") ||
                document.body;
              if (mainContent) {
                mainContent.scrollTo({
                  top: 800, // Scroll down to show accessibility section
                  behavior: "smooth",
                });
              }
            }
          }, 500); // Increased delay to ensure tab switch completes
        }
      }
    } else {
      setInitialSection("All");
    }
  }, [
    cid,
    accessibilityCategories,
    hasAccessibilityData,
    hasProcessedCidForTabSwitch,
  ]);

  const [actionToPerformAfterLogin, setActionToPerformAfterLogin] = useAtom(
    actionToPerformAfterLoginAtom,
  );
  const [actionToPerformValueAfterLogin, setActionToPerformValueAfterLogin] =
    useAtom(actionToPerformValueAfterLoginAtom);

  const isDark = useDarkMode();

  // Venue image logic (same as VenueCard)
  const { businessTypesFrontend } = useFilters();

  
  // Get photo from businessPhotoFirst or first item in businessPhotos array
  let businessPhoto = "";
  if (venueDetails?.businessPhotoFirst?.path) {
    businessPhoto = bucketUrlPrefix + venueDetails.businessPhotoFirst.path;
  } else if (venueDetails?.businessPhotos?.[0]?.path) {
    businessPhoto = bucketUrlPrefix + venueDetails.businessPhotos[0].path;
  }

  const businessphotoThumb = businessPhoto;
  const hasVenuePhoto = businessPhoto !== "";

  // Save venue state and hooks
  const [openSavedPlacesModal, setOpenSavedPlacesModal] = useState(false);
  const [savePlaceListMode, setSavePlaceListMode] = useState("");
  const [listNameCreated, setListNameCreated] = useState("");

  const {
    refetch: refetchSavedPlaces,
    isLoading: isSavedPlacesLoading,
    isFetching: isSavedPlacesFetching,
    data: savedPlacesData,
  } = useSavedPlaces();
  const savedPlaces = useMemo(
    () => savedPlacesData?.saved_places || [],
    [savedPlacesData?.saved_places],
  );

  useEffect(() => {
    if (selectedMainTab === "reviews") {
      AnalyticsService.trackGAEventHandler("postReviewViewedReviewPage");
      AnalyticsService.trackMx("postReviewViewedReviewPage", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
      if (currentReviewSort === "Latest") {
        reviews.sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
        setReviews([...reviews]);
      } else if (currentReviewSort === "Earliest") {
        reviews.sort(
          (a, b) =>
            new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime(),
        );
        setReviews([...reviews]);
      } else if (currentReviewSort === "Alphabetical") {
        reviews.sort((a, b) => {
          const nameA = a.notes.toUpperCase();
          const nameB = b.notes.toUpperCase();
          if (nameA < nameB) {
            return -1;
          }
          if (nameA > nameB) {
            return 1;
          }
          return 0;
        });
        setReviews([...reviews]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentReviewSort]);

  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin) {
      if (actionToPerformAfterLogin === "addReview") {
        setSelectedMainTab("reviews");
      } else if (actionToPerformAfterLogin === "flagReview") {
        setSelectedMainTab("reviews");
        setSelectedReview(actionToPerformValueAfterLogin);
        setIsFlagReviewOpen(true);
      } else if (actionToPerformAfterLogin === "openNotificationModal") {
        setIsNotificationModalOpen(true);
      }
      setActionToPerformAfterLogin("");
      setActionToPerformValueAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const top5Photos = getFivePhotos(venueDetails.businessPhotos);

  const handleShowLoginModal = () => {

    if(matchRoute({
      to: `/whats-nearby/$venueId`,
    })) {
      showModal(AuthModalScreen.EnterEmail);
      return;
    }
    

    // CheckIcon if the current route matches the target route
    // if venue layer redirect to venue detail and then open login modal
    // if venue detail only then no redirect needed
    if (
      !matchRoute({
        to: `/venuedetails/${venueDetails.business.slug}`,
      })
    ) {
      navigate({
        to: `/venuedetails/${venueDetails.business.slug}`,
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        search: { source: "map", showAuthModal: true } as any,
      });
    }

    showModal(AuthModalScreen.EnterEmail);
  };

  // Keyboard navigation handler for tabs
  const handleTabKeyDown = (
    e: React.KeyboardEvent, 
    currentIndex: number,
    tabRefsArray: React.RefObject<(HTMLButtonElement | null)[]>
  ) => {
    let newIndex = currentIndex;
    
    switch (e.key) {
      case 'ArrowRightIcon':
        e.preventDefault();
        newIndex = (currentIndex + 1) % mainTabs.length;
        break;
      case 'ArrowLeftIcon':
        e.preventDefault();
        newIndex = (currentIndex - 1 + mainTabs.length) % mainTabs.length;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = mainTabs.length - 1;
        break;
      default:
        return;
    }
    
    // Activate the new tab
    setSelectedMainTab(mainTabs[newIndex]);
    handleTabTracking(mainTabs[newIndex]);
    
    // Focus the new tab using the correct ref array
    tabRefsArray.current[newIndex]?.focus();
  };

  // Save venue handlers
  const handleCreateList = (listNameCreated: string) => {
    console.log("inside handleCreateList listNameCreated=", listNameCreated);
    setListNameCreated(listNameCreated);
  };

  const handleSavedPlacesModalClose = () => {
    console.log("Closing Modal handleSavedPlacesModalClose");
    setOpenSavedPlacesModal(false);
    if (savePlaceListMode === "create") {
      handleSaveVenue();
    } else {
      setListNameCreated("");
    }
  };

  const handleCreateNewClick = () => {
    console.log("handleCreateNewClick");
    setOpenSavedPlacesModal(false);
    setSavePlaceListMode("create");
    setOpenSavedPlacesModal(true);
  };

  const handleSaveVenue = async () => {
    if (!user) {
      console.log("Please login");
      return;
    }

    const { data } = await refetchSavedPlaces();
    if (!data) {
      return;
    }

    setOpenSavedPlacesModal(true);

    if (data.saved_places.length > 0) {
      console.log("list found show it");
      setSavePlaceListMode("list");
    } else {
      console.log("no list found show create list");
      setSavePlaceListMode("create");
    }
  };

  const isVenueSaved = useCallback(() => {
    if (isSavedPlacesFetching || isSavedPlacesLoading) {
      return false;
    }
    if (savedPlaces) {
      const flattened = savedPlaces
        ?.map((e) => e.saved_place_details.map((e) => e.business.id))
        .flat();
      const result = flattened.some((id) => id === venueDetails?.business.id);
      return result;
    }
    return false;
  }, [
    isSavedPlacesFetching,
    isSavedPlacesLoading,
    savedPlaces,
    venueDetails?.business.id,
  ]);

  // Handle save venue action after login
  useEffect(() => {
    if (isLoggedIn && user && actionToPerformAfterLogin === "handleSaveVenue") {
      handleSaveVenue();
      setActionToPerformAfterLogin("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionToPerformAfterLogin, user, isLoggedIn]);

  const handleFlagVenueReviewClose = () => {
    AnalyticsService.trackGAEventHandler("flagReviewReportClose");
    AnalyticsService.trackMx("flagReviewReportClose", {});
    setIsFlagReviewOpen(false);
  };

  const handleExpandSummary = useCallback((isExpanded: boolean) => {
    if (!isExpanded) {
      AnalyticsService.trackGAEventHandler("venueDetailViewLessClicked");
      AnalyticsService.trackMx("venueDetailViewLessClicked", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else {
      AnalyticsService.trackGAEventHandler("venueDetailViewMoreClicked");
      AnalyticsService.trackMx("venueDetailViewMoreClicked", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    }
  }, [venueDetails]);

  const handleAddNoteModalClose = () => {
    AnalyticsService.trackGAEventHandler("mapperNoteReportClose");
    AnalyticsService.trackMx("mapperNoteReportClose", {});
    setAddNoteOpenModal(false);
  };

  const handleAddReviewClose = () => {
    setIsAddReviewOpen(false);
  };

  const handleNotificationClick = (preSelectType?: 'accessibility' | 'liveAlerts') => {
    if (!isLoggedIn) {
      // Set action to perform after login
      setActionToPerformAfterLogin("openNotificationModal");
      // Show login modal
      showModal(AuthModalScreen.EnterEmail);
    } else {
      // Store which type to pre-select if provided
      if (preSelectType) {
        setPreSelectedNotificationType(preSelectType);
      }
      setIsNotificationModalOpen(true);
    }
  };

  const handleNotificationModalClose = () => {
    setIsNotificationModalOpen(false);
    setPreSelectedNotificationType(null);
  };

  const handleNotificationModalSuccess = () => {
    // The query will auto-refetch due to invalidation in useSyncBusinessInterest
    // We can optionally trigger a manual refetch as well, but it's not strictly necessary
    // Use a small delay to ensure the mutation completes before refetching
    setTimeout(() => {
      onRefresh();
    }, 300);
  };

  const handleNotificationModalComplete = () => {
    // Called when the success modal closes - trigger a refresh to update the UI
    setTimeout(() => {
      onRefresh();
    }, 100);
  };

  // Get current user's interest data
  const currentUserInterest = useMemo(() => {
    if (!user?.id || !venueDetails.businessInterestData) {
      return null;
    }
    return (
      venueDetails.businessInterestData.find(
        (interest) => interest?.user?.id === user?.id,
      ) || null
    );
  }, [user?.id, venueDetails.businessInterestData]);

  const isNotificationEnabled = () => {
    return (
      currentUserInterest &&
      (currentUserInterest.notify_accessibility_information === 1 ||
        currentUserInterest.notify_live_alert === 1)
    );
  };

  // Separate notification state checkers for each component
  const isAccessibilityInfoNotificationEnabled = () => {
    return (
      currentUserInterest &&
      currentUserInterest.notify_accessibility_information === 1
    );
  };

  const isLiveAlertNotificationEnabled = () => {
    return (
      currentUserInterest &&
      currentUserInterest.notify_live_alert === 1
    );
  };

  const handleTabTracking = (selectedTab) => {
    console.log("Tab changed to:", selectedTab);
    if (selectedTab === "accessibility") {
      AnalyticsService.trackGAEventHandler(
        "navigatedToAccessibilityInformation",
      );
      AnalyticsService.trackMx("navigatedToAccessibilityInformation", {});
      AnalyticsService.trackGAEventHandler("venueDetailAccessibilityTabClicked");
      AnalyticsService.trackMx("venueDetailAccessibilityTabClicked", {
        VenueId: venueDetails.business.id,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (selectedTab === "photos") {
      AnalyticsService.trackGAEventHandler("navigatedToPhotosInformation");
      AnalyticsService.trackMx("navigatedToPhotosInformation", {});
      AnalyticsService.trackGAEventHandler("venueDetailPhotosTabClicked");
      AnalyticsService.trackMx("venueDetailPhotosTabClicked", {
        VenueId: venueDetails.business.id,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (selectedTab === "alerts") {
      AnalyticsService.trackGAEventHandler("navigatedToAlertsInformation");
      AnalyticsService.trackMx("navigatedToAlertsInformation", {});
      AnalyticsService.trackGAEventHandler("venueDetailAlertsTabClicked");
      AnalyticsService.trackMx("venueDetailAlertsTabClicked", {
        VenueId: venueDetails.business.id,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (selectedTab === "reviews") {
      AnalyticsService.trackGAEventHandler("navigatedToReviewsInformation");
      AnalyticsService.trackMx("navigatedToReviewsInformation", {});
      AnalyticsService.trackGAEventHandler("venueDetailExperiencesTabClicked");
      AnalyticsService.trackMx("venueDetailExperiencesTabClicked", {
        VenueId: venueDetails.business.id,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (selectedTab === "about") {
      AnalyticsService.trackGAEventHandler("navigatedToAboutInformation");
      AnalyticsService.trackMx("navigatedToAboutInformation", {});
      AnalyticsService.trackGAEventHandler("venueDetailAboutTabClicked");
      AnalyticsService.trackMx("venueDetailAboutTabClicked", {
        VenueId: venueDetails.business.id,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (selectedTab === "mapperNotes") {
      AnalyticsService.trackGAEventHandler("navigatedToMapperNotesInformation");
      AnalyticsService.trackMx("navigatedToMapperNotesInformation", {});
    } else if (selectedTab === "nearbyvenues") {
      AnalyticsService.trackGAEventHandler(
        "navigatedToNearbyVenuesInformation",
      );
      AnalyticsService.trackMx("navigatedToNearbyVenuesInformation", {});
    }
  };

  const handleReviewSort = (value) => {
    console.log("handleReviewSort value", value);

    if (value === "Latest") {
      AnalyticsService.trackGAEventHandler("sortedReviewsByLatest");
      AnalyticsService.trackMx("sortedReviewsByLatest", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else if (value === "Earliest") {
      AnalyticsService.trackGAEventHandler("sortedReviewsByEarliest");
      AnalyticsService.trackMx("sortedReviewsByEarliest", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    } else {
      AnalyticsService.trackGAEventHandler("sortedReviewsByAlphabetical");
      AnalyticsService.trackMx("sortedReviewsByAlphabetical", {
        VenueId: venueDetails.business.id,
        VenueBrandId: venueDetails.brandId,
        VenueBrandName: venueDetails.brandName,
        VenueAddress: venueDetails.address,
        VenueName: venueDetails.name,
        VenueType: venueDetails.businessType,
      });
    }

    setCurrentReviewSort(value as any);
  };

  const showEditNote = (note: any) => {
    setEditVenueNoteOpenModal(true);
    setEditNote(note);
  };

  const showFlagNote = (note: any) => {
    console.log("showFlagNote", note);
    setFlagVenueNoteOpenModal(true);
    setFlagNote(note);
  };

  const handleOpenFullImage = (image) => {
    setCurrentImage(image);
    setFullImageView(true);

    // Track opening the full image viewer
    AnalyticsService.trackGAEventHandler("openPhotoLightbox");
    AnalyticsService.trackMx("openPhotoLightbox", {
      VenueId: venueDetails.business.id,
      VenueBrandId: venueDetails.brandId,
      VenueBrandName: venueDetails.brandName,
      VenueAddress: venueDetails.address,
      VenueName: venueDetails.name,
      VenueTypes: venueDetails.businessType,
    });
  };

  useEffect(() => {
    // Add Summernote CSS
    const style = document.createElement("style");
    style.innerHTML = `
 .summernote-content {
 /* Base styles */
 font-family: inherit;
 line-height: 1.5;
 color: inherit;
 }
 
 /* Headings */
 .summernote-content h1, .summernote-content h2, .summernote-content h3,
 .summernote-content h4, .summernote-content h5, .summernote-content h6 {
 margin-top: 0.5em;
 margin-bottom: 0.5em;
 font-weight: bold;
 line-height: 1.2;
 }
 .summernote-content h1 { font-size: 2rem; }
 .summernote-content h2 { font-size: 1.5rem; }
 .summernote-content h3 { font-size: 1.25rem; }
 .summernote-content h4 { font-size: 1.1rem; }
 .summernote-content h5, .summernote-content h6 { font-size: 1rem; }
 
 /* Lists */
 .summernote-content ul, .summernote-content ol {
 padding-left: 2em;
 margin-top: 0.5em;
 margin-bottom: 0.5em;
 }
 .summernote-content ul { list-style-type: disc; }
 .summernote-content ol { list-style-type: decimal; }
 .summernote-content ul ul { list-style-type: circle; }
 .summernote-content ul ul ul { list-style-type: square; }
 
 /* Paragraphs and spacing */
 .summernote-content p {
 margin-bottom: 0.75em;
 }
 
 /* Links */
 .summernote-content a {
 color: #0066cc;
 text-decoration: underline;
 }
 
 /* Tables */
 .summernote-content table {
 border-collapse: collapse;
 width: 100%;
 margin: 1em 0;
 }
 .summernote-content th, .summernote-content td {
 border: 1px solid #ddd;
 padding: 8px;
 text-align: left;
 }
 
 /* Blockquotes */
 .summernote-content blockquote {
 margin: 1em 0;
 padding: 0.5em 1em;
 border-left: 5px solid #eee;
 color: #666;
 }
 
 /* Code blocks */
 .summernote-content pre {
 background-color: #f5f5f5;
 padding: 1em;
 border-radius: 4px;
 overflow-x: auto;
 }
 .summernote-content code {
 font-family: monospace;
 background-color: #f5f5f5;
 padding: 0.2em 0.4em;
 border-radius: 3px;
 }
 
 /* Images */
 .summernote-content img {
 max-width: 100%;
 height: auto;
 }
 
 /* Alignment classes that Summernote uses */
 .summernote-content .text-left { text-align: left; }
 .summernote-content .text-center { text-align: center; }
 .summernote-content .text-right { text-align: right; }
 .summernote-content .text-justify { text-align: justify; }
 
 /* Float classes */
 .summernote-content .float-left {
 float: left;
 margin-right: 1em;
 margin-bottom: 0.5em;
 }
 .summernote-content .float-right {
 float: right;
 margin-left: 1em;
 margin-bottom: 0.5em;
 }
 
 /* Ensure proper spacing between elements */
 .summernote-content * + h1,
 .summernote-content * + h2,
 .summernote-content * + h3,
 .summernote-content * + h4,
 .summernote-content * + h5,
 .summernote-content * + h6 {
 margin-top: 1em;
 }`;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Function to handle dynamic tab content
  const renderDynamicTabContent = (tabId: string) => {
    const id = parseInt(tabId.split("_")[1]);
    const tab = dynamicTabs.find((t) => t.id === id);

    if (!tab) return null;

    const orderedBlocks = (tab.blocks ?? [])
      .slice()
      .sort((a, b) => a.order - b.order);

    // Flat list of every image across blocks — drives the lightbox so
    // navigation works across block boundaries.
    const allImages = orderedBlocks.flatMap((block) =>
      (block.images ?? []).slice().sort((a, b) => a.order - b.order),
    );

    return (
      <div className="flex flex-col">
        <div className="mt-2">
          {/* Cover photo banner */}
          {tab.cover_photo && (
            <div className="w-full h-[220px] md:h-[320px] rounded-lg overflow-hidden mb-4">
              <img
                alt={tab.cover_photo_alt_text || tab.title}
                className="w-full h-full object-cover"
                src={bucketUrlPrefix + tab.cover_photo}
                loading="lazy"
              />
            </div>
          )}

          {/* Tab-level description */}
          {tab.description && (
            <p className="label-m-medium text-fg-neutral-strong mb-6">
              {tab.description}
            </p>
          )}

          {orderedBlocks.map((block) => {
            const blockImages = (block.images ?? [])
              .slice()
              .sort((a, b) => a.order - b.order);

            return (
              <section key={block.id} className="mb-8">
                {block.title && (
                  <h3 className="title-s-bold text-fg-neutral-strong mb-3">
                    {block.title}
                  </h3>
                )}

                {block.content && (
                  <div
                    className="label-m-medium text-fg-neutral-strong pb-4 dynamic-tab-content summernote-content"
                    dangerouslySetInnerHTML={{ __html: block.content }}
                  />
                )}

                {blockImages.length > 0 && (
                  <div className="photo-grid-container relative mt-4">
                    <RowsPhotoAlbum
                      photos={blockImages.map((image, index) => {
                        const dimensions = photoDimensions[image.image_path] || {
                          width: 200,
                          height: 200,
                        };
                        return {
                          src: bucketUrlPrefix + image.image_path,
                          width: dimensions.width,
                          height: dimensions.height,
                          alt:
                            image.alt_text ||
                            `${block.title || tab.title} image ${index + 1}`,
                        };
                      })}
                      spacing={8}
                      targetRowHeight={200}
                      rowConstraints={(containerWidth) => {
                        let maxPhotos = 2;
                        if (containerWidth > 1024) {
                          maxPhotos = 4;
                        } else if (containerWidth > 768) {
                          maxPhotos = 3;
                        }
                        return {
                          maxPhotos,
                          minPhotos: 1,
                          singleRowMaxHeight: 350,
                        };
                      }}
                      onClick={({ index }) => {
                        handleOpenFullImage(blockImages[index].image_path);
                      }}
                      render={{
                        button: (props, context) => {
                          const { index } = context;
                          const img = blockImages[index];
                          const altText = img.alt_text || "";
                          const ariaLabel = altText
                            ? `Photo ${index + 1} of ${blockImages.length}. ${altText}`
                            : `Photo ${index + 1} of ${blockImages.length}`;

                          return (
                            <button
                              {...props}
                              aria-label={ariaLabel}
                              className={`${props.className} focus:outline-none rounded relative overflow-hidden group`}
                              style={{
                                ...props.style,
                              }}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" || e.key === " ") {
                                  e.preventDefault();
                                  handleOpenFullImage(img.image_path);
                                }
                              }}
                            >
                              {props.children}
                              <div className="absolute inset-0 bg-[#070708] opacity-0 group-hover:opacity-[0.12] group-active:opacity-[0.12] transition-opacity duration-200 pointer-events-none rounded" />
                            </button>
                          );
                        },
                      }}
                    />
                  </div>
                )}
              </section>
            );
          })}

          {/* Full image view spans every block's images so prev/next walks the whole tab */}
          {fullImageView &&
            currentImage &&
            allImages.some((img) => img.image_path === currentImage) && (
              <FullImageViewer
                images={allImages.map((image, index) => ({
                  src: bucketUrlPrefix + image.image_path,
                  alt: image.alt_text || `${tab.title} image ${index + 1}`,
                  tag_name: tab.title,
                  category_name: tab.title,
                  created_at: null,
                  photo_type: "tab",
                  review_photos_id: null,
                  width: photoDimensions[image.image_path]?.width || 800,
                  height: photoDimensions[image.image_path]?.height || 600,
                  sort_order: image.order,
                  qtags: [],
                  tags: [
                    {
                      tagName: tab.title,
                      categoryDisplaySectionId: "",
                    },
                  ],
                  displaySections: {},
                  venueId: venueDetails.business.id,
                  venueName: venueDetails.name,
                  venueAddress: venueDetails.address,
                  venueBrandId: venueDetails.brandId,
                  venueBrandName: venueDetails.brandName,
                  venueTypes: venueDetails.businessType,
                }))}
                currentIndex={allImages.findIndex(
                  (img) => img.image_path === currentImage,
                )}
                onClose={() => setFullImageView(false)}
                onFlag={() => {}}
                showFlag={false}
                showRightTab={false}
              />
            )}
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Show loader when syncing interest */}
      {isSyncingInterest && <Loader aria-label="Updating notification preferences" />}
      
      {/* Accessibility Alerts Page - Full Responsive Layout */}
      {/* Mobile-first design with lg: breakpoints for desktop 3-column layout */}
      {/* VENUE CARD and Tab Navigation are static, only TAB CONTENT scrolls */}

      <div className="bg-bg-brand-secondary-default h-full flex flex-col overflow-y-auto scrollbar-hide lg:overflow-hidden">
        {/* ===== MAIN CONTENT ===== */}
        <div className="flex-1 flex flex-col lg:overflow-y-hidden p-4 md:p-0 md:pl-4 md:pb-4">
          {/* Back Navigation (Mobile) */}
          <div className="flex lg:hidden px-2 border-b border-border-token-brand-minimal shrink-0">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 py-4 group cursor-pointer"
            >
              <ArrowLeftIcon size={20} aria-hidden="true" className="!text-fg-brand-subtle" />
              <p className="ml-1 label-m-semibold !text-fg-brand-subtle">
                {source === "notifications-page" ||
                String(source) === "notifications-page"
                  ? i18n.get("backToAllNotifications") ||
                    "Back to all notifications"
                  : source === "notification-settings" ||
                      String(source) === "notification-settings"
                    ? i18n.get("backToSettings") || "Back to settings"
                    : currentVenueTypePage === 1
                      ? i18n.get("backToAllResults")
                      : i18n.get("explore")}
              </p>
            </button>
          </div>

          {/* Content Area - Responsive Grid */}
          <div className="flex-1 flex flex-col lg:flex-row overflow-hidden gap-4">
            {/* ===== LEFT COLUMN (Desktop only) ===== */}
            <div className="hidden lg:flex lg:flex-col lg:w-[290px] lg:shrink-0 bg-bg-brand-secondary-default">
              {/* Back Button - Desktop Left Column */}
              <div className="border-b border-border-token-brand-minimal shrink-0 bg-bg-brand-secondary-default">
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 py-3 group cursor-pointer"
                >
                  <ArrowLeftIcon size={20} aria-hidden="true" className="!text-fg-brand-subtle" />
                  <p className="ml-1 label-m-semibold !text-fg-brand-subtle">
                    {source === "notifications-page" ||
                    String(source) === "notifications-page"
                      ? i18n.get("backToAllNotifications") ||
                        "Back to all notifications"
                      : source === "notification-settings" ||
                          String(source) === "notification-settings"
                        ? i18n.get("backToSettings") || "Back to settings"
                        : currentVenueTypePage === 1
                          ? i18n.get("backToAllResults")
                          : i18n.get("explore")}
                  </p>
                </button>
              </div>
              
              {/* Sidebar - Desktop Left Column */}
              <article className="flex-1 flex flex-col gap-4 overflow-y-auto md:mt-4">
                {/* Venue Image with Action Buttons */}
                <VenueImageWithActions
                  businessPhoto={businessPhoto}
                  businessphotoThumb={businessphotoThumb}
                  venueName={venueDetails.name}
                  venueSlug={venueDetails.business.slug}
                  hasVenuePhoto={hasVenuePhoto}
                  isVenueSaved={isVenueSaved}
                  handleSaveVenue={handleSaveVenue}
                  isLoggedIn={isLoggedIn}
                  setActionToPerformAfterLogin={setActionToPerformAfterLogin}
                  isDark={isDark}
                  onNotificationClick={handleNotificationClick}
                  isNotificationEnabled={isNotificationEnabled}
                  venueDetails={venueDetails}
                />

                {/* Venue Details - Desktop only */}
                <div className="flex flex-col gap-2">

                  <div className="flex flex-col gap-2">
                    {/* Group 1: Name + Location (tightly grouped) */}
                    <div className="flex flex-col gap-1">
                      <h1 className="label-l-bold text-fg-neutral-strong">
                        {venueDetails.name}
                      </h1>
                      {(locationOnly || venueDetails.businessType) && (
                        <div className="flex items-baseline flex-wrap gap-x-1">
                          {locationOnly && (
                            <p className="label-s-medium text-fg-neutral-subtle">
                              {locationOnly}
                              {venueDetails.businessType && (
                                <span className="text-fg-neutral-subtle"> •</span>
                              )}
                            </p>
                          )}
                          {venueDetails.businessType && (
                            <h2 className="label-s-medium text-fg-neutral-subtle">
                              {venueDetails.businessType}
                            </h2>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Meta InfoIcon (Distance, Hours) */}
                    <p className="label-s-medium text-fg-neutral-subtle">
                      <span>{venueDetails.distance} mile</span>
                      {/* Temporarily Closed Status */}
                      {venueDetails.businessStatus === "CLOSED_TEMPORARILY" && (
                        <>
                          <span className="text-fg-neutral-minimal"> | </span>
                          <span className="label-s-medium text-fg-error-subtle">{i18n.get("temporarilyClosed")}</span>
                        </>
                      )}
                      {/* Opening Hours (arrow focusable, if present) - only show if NOT temporarily closed */}
                      {venueDetails.businessStatus !== "CLOSED_TEMPORARILY" && venueDetails.openingHours &&
                        venueDetails.openingHours[0]?.mode === 0 && (
                          <>
                            <span className="text-fg-neutral-minimal"> | </span>
                            <OpeningHours
                              openingHours={venueDetails.openingHours}
                              containerClassName=""
                              className="label-s-medium text-fg-neutral-subtle"
                            />
                          </>
                      )}
                    </p>

                    {/* Group 3: Reviews + Verified */}
                    <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1">

                      {reviews.length === 0 && (
                        <>
                          <div className="flex items-center gap-1">
                            <img src="/images/Leaf.svg" alt="" aria-hidden="true" className="w-5 h-5" />
                            <button
                              onClick={() => {
                                if (isLoggedIn) {
                                  setIsAddReviewOpen(true);
                                } else {
                                  handleShowLoginModal();
                                }
                              }}>
                              <span className="label-s-medium text-fg-neutral-subtle underline leading-none">
                                {i18n.get("beTheFirstToShareYourExperience")}
                              </span>
                            </button>
                          </div>
                        </>
                      )}

                      {reviews.length > 0 && reviews.length < 3 && (
                        <div className="flex items-center gap-1">
                          <img src="/images/Plant.svg" alt="" aria-hidden="true" className="w-5 h-5" />
                          <span className="label-s-medium text-fg-neutral-subtle leading-none">
                            {i18n.get("stillTasting")}
                          </span>
                          <button
                            className="inline-flex items-center leading-none"
                            onClick={() => {
                              AnalyticsService.trackGAEventHandler("venueDetailTastingReviewsClicked");
                              AnalyticsService.trackMx("venueDetailTastingReviewsClicked", {
                                VenueId: venueDetails.business.id,
                                VenueBrandId: venueDetails.brandId,
                                VenueBrandName: venueDetails.brandName,
                                VenueAddress: venueDetails.address, 
                                VenueName: venueDetails.name,
                                VenueType: venueDetails.businessType,
                                AlertCount: accessibilityAlerts.length,
                              });
                              setSelectedMainTab("reviews");
                            }}>
                            <span className="label-s-medium text-fg-neutral-subtle underline leading-none">({venueDetails?.venueAccessibilityExperienceStats?.totalCount} {reviews.length === 1 ? i18n.get("experience") : i18n.get("experiences")})</span>
                          </button>
                        </div>
                      )}

                      {reviews.length > 0 && reviews.length >= 3 && (() => {
                        const coolPercentage = venueDetails?.venueAccessibilityExperienceStats?.coolPercentage ?? 0;
                        const weirdPercentage = venueDetails?.venueAccessibilityExperienceStats?.weirdPercentage ?? 0;
                        const isSweet = coolPercentage >= weirdPercentage;
                        
                        return (
                          <div className="flex items-center gap-1">
                            <img
                              src={isSweet
                                ? "/images/Orange.svg"
                                : "/images/OrangeSlice.svg"
                              }
                              alt=""
                              aria-hidden="true"
                              className="w-5 h-5"
                            />
                            <span className="label-s-regular text-fg-neutral-subtle leading-none">
                              {i18n.get(isSweet ? "sweet" : "sour")} • <span className="label-s-regular text-fg-neutral-subtle">{Math.round(coolPercentage)}% {i18n.get("sweetness")}</span>
                            </span>
                            <button
                              className="inline-flex items-center leading-none"
                              onClick={() => {
                                AnalyticsService.trackGAEventHandler("venueDetailTastingReviewsClicked");
                                AnalyticsService.trackMx("venueDetailTastingReviewsClicked", {
                                  VenueId: venueDetails.business.id,
                                  VenueBrandId: venueDetails.brandId,
                                  VenueBrandName: venueDetails.brandName,
                                  VenueAddress: venueDetails.address,
                                  VenueName: venueDetails.name,
                                  VenueType: venueDetails.businessType,
                                  AlertCount: accessibilityAlerts.length,
                                });
                                setSelectedMainTab("reviews");  
                              }}>
                              <span className="label-s-regular text-fg-neutral-subtle underline leading-none">({venueDetails?.venueAccessibilityExperienceStats?.totalCount} {reviews.length === 1 ? i18n.get("experience") : i18n.get("experiences")})</span>
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                    {/* Sociability Verified */}
                    {venueDetails.businessVerified != null && (
                      <div className="flex items-center gap-1">
                        <img src="/images/SealCheck.svg" alt="" aria-hidden="true" className="w-5 h-5 shrink-0" />
                        <span className="label-m-semibold text-fg-neutral-subtle whitespace-nowrap">
                          {venueDetails.businessVerified}
                        </span>
                        {venueDetails.businessVerifiedDatetime && (
                          <span className="label-s-medium text-fg-neutral-subtle whitespace-nowrap">
                            {`: ${new Date(venueDetails.businessVerifiedDatetime.replace(" ", "T")).toLocaleDateString("en-GB", { month: "long", year: "numeric" })}`}
                          </span>
                        )}
                      </div>
                    )}
                    </div>

                  </div>
                </div>

                {(accessibilityAlerts.length > 0 || venueDetails.business.venue_summary) && (
                  <div className="flex flex-col gap-2">

                    {/* Accessibility Alerts - Only show if there are alerts */}
                    {accessibilityAlerts.length > 0 && (
                      <div className="bg-bg-error-tertiary-default rounded-lg border border-border-token-error-minimal hover:bg-bg-error-tertiary-hover">
                        <button
                          onClick={() => {
                            AnalyticsService.trackGAEventHandler("venueDetailAccessibilityAlertsClicked");
                            AnalyticsService.trackMx("venueDetailAccessibilityAlertsClicked", {
                              VenueId: venueDetails.business.id,
                              VenueBrandId: venueDetails.brandId,
                              VenueBrandName: venueDetails.brandName,
                              VenueAddress: venueDetails.address,
                              VenueName: venueDetails.name,
                              VenueType: venueDetails.businessType,
                              AlertCount: accessibilityAlerts.length,
                            });
                            setSelectedMainTab("alerts");
                          }}
                          className="w-full flex items-center justify-between p-3 hover:bg-error-100 transition-colors rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            <WarningIcon
                              size={24}
                              weight="fill"
                              aria-hidden="true"
                              className="text-fg-error-subtle"
                            />
                            <span className="label-m-semibold text-fg-neutral-strong">
                              {i18n.get("accessibilityAlerts")}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="label-s-medium text-fg-neutral-strong">
                              {accessibilityAlerts.length}
                            </span>
                            <CaretRightIcon
                              size={16}
                              weight="bold"
                              aria-hidden="true"
                              className="text-fg-neutral-strong"
                            />
                          </div>
                        </button>
                      </div>
                    )}

                    {/* description */}
                    {venueDetails.business.venue_summary && (
                      <VenueSummary 
                        summary={venueDetails.business.venue_summary} 
                        onExpand={handleExpandSummary}
                      />
                    )}               
                  
                  </div>
                )}

                <div className="flex flex-col gap-2">
                 
                  {/* Share Experience Button */}
                  <button
                    onClick={() => {
                      if (isLoggedIn) {
                        setIsAddReviewOpen(true);
                      } else {
                        handleShowLoginModal();
                      }
                    }}
                    className="mt-2 mb-2 flex items-center justify-center gap-2 w-full h-11 bg-bg-brand-primary-default
                    rounded-lg label-m-semibold hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active
                    transition-all duration-200 focus-blue
                    min-h-[44px]"
                  >
                    <PlusCircleIcon size={20} aria-hidden="true" className="text-fg-neutral-onFilled" />
                    <span className="label-m-medium text-fg-neutral-onFilled">{i18n.get("shareYourExperienceShort")}</span>
                  </button>

                  {/* Claim Business Link */}
                  <VenueInfoClaim
                    venueDetailsResponse={venueDetailsResponse}
                    onRefresh={onRefresh}
                  />

                  <AddReviewModal
                    venueId={venueDetails.business.id.toString()}
                    venueName={venueDetails.name}
                    isOpen={isAddReviewOpen}
                    onClose={handleAddReviewClose}
                    venueDetails={venueDetails}
                    onSuccess={() => setSelectedMainTab("reviews")}
                  />
                </div>

              </article>
            </div>

            {/* ===== RIGHT COLUMN (All viewports) ===== */}
            <div className="flex-1 flex flex-col overflow-hidden bg-bg-brand-secondary-default">
              
              {/* Mobile-only article */}
              <article className="lg:hidden w-full flex flex-col gap-2 overflow-y-auto md:pr-0">
                {/* Venue Image with Action Buttons */}
                <VenueImageWithActions
                  businessPhoto={businessPhoto}
                  businessphotoThumb={businessphotoThumb}
                  venueName={venueDetails.name}
                  venueSlug={venueDetails.business.slug}
                  hasVenuePhoto={hasVenuePhoto}
                  isVenueSaved={isVenueSaved}
                  handleSaveVenue={handleSaveVenue}
                  isLoggedIn={isLoggedIn}
                  setActionToPerformAfterLogin={setActionToPerformAfterLogin}
                  isDark={isDark}
                  onNotificationClick={handleNotificationClick}
                  isNotificationEnabled={isNotificationEnabled}
                  venueDetails={venueDetails}
                />

                {/* Venue Details - Mobile only */}
                <div className="flex flex-col gap-2">
                  {reviews.length === 0 && (
                    <>
                      <div className="flex items-center gap-1">
                        <img src="/images/Leaf.svg" alt="" aria-hidden="true" className="w-5 h-5" />
                        <button
                          className="inline-flex items-center leading-none"
                          onClick={() => {
                            if (isLoggedIn) {
                              setIsAddReviewOpen(true);
                            } else {
                              handleShowLoginModal();
                            }
                          }}>
                          <span className="label-s-medium text-fg-neutral-subtle underline leading-none">
                            {i18n.get("beTheFirstToShareYourExperience")}
                          </span>
                        </button>
                      </div>
                    </>
                  )}

                  {reviews.length > 0 && reviews.length < 3 && (
                    <div className="flex items-center gap-1">
                      <img src="/images/Plant.svg" alt="" aria-hidden="true" className="w-5 h-5" />
                      <span className="label-s-medium text-fg-neutral-subtle leading-none">
                        {i18n.get("stillTasting")}
                      </span>
                      <button
                        className="inline-flex items-center leading-none"
                        onClick={() => {
                          AnalyticsService.trackGAEventHandler("venueDetailTastingReviewsClicked");
                          AnalyticsService.trackMx("venueDetailTastingReviewsClicked", {
                            VenueId: venueDetails.business.id,
                            VenueBrandId: venueDetails.brandId,
                            VenueBrandName: venueDetails.brandName,
                            VenueAddress: venueDetails.address,
                            VenueName: venueDetails.name,
                            VenueType: venueDetails.businessType,
                            AlertCount: accessibilityAlerts.length,
                          });
                          setSelectedMainTab("reviews");
                        }}>
                        <span className="label-s-medium text-fg-neutral-subtle underline leading-none">({venueDetails?.venueAccessibilityExperienceStats?.totalCount} {reviews.length === 1 ? i18n.get("experience") : i18n.get("experiences")})</span>
                      </button>
                    </div>
                  )}

                  {reviews.length > 0 && reviews.length >= 3 && (() => {
                    const coolPercentage = venueDetails?.venueAccessibilityExperienceStats?.coolPercentage ?? 0;
                    const isSweet = coolPercentage >= 60;
                    
                    return (
                      <div className="flex items-center gap-1">
                        <img
                          src={isSweet
                            ? "/images/Orange.svg"
                            : "/images/OrangeSlice.svg"
                          }
                          alt=""
                          aria-hidden="true"
                          className="w-5 h-5"
                        />
                        <span className="label-s-regular text-fg-neutral-subtle leading-none">
                          {i18n.get(isSweet ? "sweet" : "sour")} • <span className="label-s-regular text-fg-neutral-subtle">{Math.round(coolPercentage)}% {i18n.get("sweetness")}</span>
                        </span>
                        <button
                          className="inline-flex items-center leading-none"
                          onClick={() => {
                            AnalyticsService.trackGAEventHandler("venueDetailTastingReviewsClicked");
                            AnalyticsService.trackMx("venueDetailTastingReviewsClicked", {
                              VenueId: venueDetails.business.id,
                              VenueBrandId: venueDetails.brandId,
                              VenueBrandName: venueDetails.brandName,
                              VenueAddress: venueDetails.address,
                              VenueName: venueDetails.name,
                              VenueType: venueDetails.businessType,
                              AlertCount: accessibilityAlerts.length,
                            });
                            setSelectedMainTab("reviews");  
                          }}>
                          <span className="label-s-regular text-fg-neutral-subtle underline leading-none">({venueDetails?.venueAccessibilityExperienceStats?.totalCount} {reviews.length === 1 ? i18n.get("experience") : i18n.get("experiences")})</span>
                        </button>
                      </div>
                    );
                  })()}
                </div>

                <div className="flex flex-col gap-2">
                  {/* Share Experience Button */}
                  <button
                    onClick={() => {
                      if (isLoggedIn) {
                        setIsAddReviewOpen(true);
                      } else {
                        handleShowLoginModal();
                      }
                    }}
                    className="mt-2 mb-2 flex items-center justify-center gap-2 w-full h-11 bg-bg-brand-primary-default
                    rounded-lg label-m-semibold hover:bg-bg-brand-primary-hover active:bg-bg-brand-primary-active
                    transition-all duration-200 focus-blue
                    min-h-[44px]"
                  >
                    <PlusCircleIcon size={20} aria-hidden="true" className="text-fg-neutral-onFilled" />
                    <span className="label-m-medium text-fg-neutral-onFilled">{i18n.get("shareYourExperienceShort")}</span>
                  </button>

                  {/* Claim Business Link */}
                  <VenueInfoClaim
                    venueDetailsResponse={venueDetailsResponse}
                    onRefresh={onRefresh}
                  />
                </div>
              </article>

              {/* Desktop Tabs */}
              <div className="hidden lg:block border-b border-border-token-brand-minimal shrink-0">
                <ScrollContainer
                  className="w-full"
                  component="nav"
                  aria-label="Venue details tabs"
                >
                  <div className="flex items-center w-max min-w-full" role="tablist">
                    {mainTabs.map((tab, index) => (
                      <button
                        key={tab}
                        ref={(el) => {
                          if (el) desktopTabRefs.current[index] = el;
                        }}
                        role="tab"
                        aria-selected={selectedMainTab === tab}
                        aria-controls={`tabpanel-${tab}`}
                        tabIndex={selectedMainTab === tab ? 0 : -1}
                        id={`desktop-tab-${tab}`}
                        onClick={() => {
                          setSelectedMainTab(tab);
                          handleTabTracking(tab);
                          tabContentScrollRef.current?.scrollTo({ top: 0 });
                        }}
                        onKeyDown={(e) => handleTabKeyDown(e, index, desktopTabRefs)}
                        className={cn(
                          "relative px-6 whitespace-nowrap min-w-[44px] min-h-[44px] transition-colors flex-shrink-0 bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                          selectedMainTab === tab
                            ? "label-m-semibold text-fg-brand-subtle"
                            : "label-m-medium text-fg-neutral-subtle",
                        )}
                      >
                        <span className="flex items-center gap-1.5">
                          {mainTabItems[index].label}
                          {mainTabItems[index].badgeCount > 0 && (
                            <span
                              className={cn(
                                "flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 label-s-medium",
                                selectedMainTab === tab
                                  ? "bg-container-brand-subtle text-fg-neutral-onFilled"
                                  : "bg-container-neutral-subtle text-fg-neutral-onFilled",
                              )}
                              aria-label={`${mainTabItems[index].badgeCount} items`}
                            >
                              {mainTabItems[index].badgeCount > 99 ? "99+" : mainTabItems[index].badgeCount}
                            </span>
                          )}
                        </span>
                        {selectedMainTab === tab && (
                          <span
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default"
                            aria-hidden="true"
                          ></span>
                        )}
                      </button>
                    ))}
                  </div>
                </ScrollContainer>
              </div>

              {/* Mobile Tabs */}
              <ScrollContainer
                className="lg:hidden border-b border-border-token-brand-minimal shrink-0"
                component="nav"
                aria-label="Venue details tabs"
              >
                <div className="flex items-center w-max min-w-full" role="tablist">
                  {mainTabs.map((tab, index) => (
                    <button
                      key={tab}
                      ref={(el) => {
                        if (el) mobileTabRefs.current[index] = el;
                      }}
                      role="tab"
                      aria-selected={selectedMainTab === tab}
                      aria-controls={`tabpanel-${tab}`}
                      tabIndex={selectedMainTab === tab ? 0 : -1}
                      id={`mobile-tab-${tab}`}
                      onClick={() => {
                        setSelectedMainTab(tab);
                        handleTabTracking(tab);
                        tabContentScrollRef.current?.scrollTo({ top: 0 });
                      }}
                      onKeyDown={(e) => handleTabKeyDown(e, index, mobileTabRefs)}
                      className={cn(
                        "relative px-6 whitespace-nowrap min-w-[44px] min-h-[44px] transition-colors flex-shrink-0 bg-bg-brand-ghost-default hover:bg-bg-brand-ghost-hover",
                        selectedMainTab === tab
                          ? "label-m-semibold text-fg-brand-subtle"
                          : "label-m-medium text-fg-neutral-subtle",
                      )}
                    >
                      <span className="flex items-center gap-1.5">
                        {mainTabItems[index].label}
                        {mainTabItems[index].badgeCount > 0 && (
                          <span
                            className={cn(
                              "flex items-center justify-center min-w-[20px] h-5 rounded-full px-1.5 label-s-medium",
                              selectedMainTab === tab
                                ? "bg-container-brand-subtle text-fg-neutral-onFilled"
                                : "bg-container-neutral-subtle text-fg-neutral-onFilled",
                            )}
                            aria-label={`${mainTabItems[index].badgeCount} items`}
                          >
                            {mainTabItems[index].badgeCount > 99 ? "99+" : mainTabItems[index].badgeCount}
                          </span>
                        )}
                      </span>
                      {selectedMainTab === tab && (
                        <span
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-bg-brand-primary-default"
                          aria-hidden="true"
                        ></span>
                      )}
                    </button>
                  ))}
                </div>
              </ScrollContainer>

              {/* Tab Panel Content */}
              <div className="flex-1 flex flex-col w-full min-h-0 lg:p-0 lg:mt-4  md:pr-0">
                {/* Main scrollable container with border */}
                <div
                  ref={tabContentScrollRef}
                  className="flex-1 overflow-y-auto md:border [scrollbar-gutter:stable_both-edges]
                  md:border-border-token-brand-minimal md:rounded-xl bg-bg-brand-secondary-default"
                  aria-label="Venue details content"
                >
                  {/* [scrollbar-gutter:stable_both-edges] */}
                  {/* Tab Content Section */}
                  <section
                    className="flex flex-col  min-h-full md:p-4"
                    role="tabpanel"
                    id={`tabpanel-${selectedMainTab}`}
                    aria-labelledby={`desktop-tab-${selectedMainTab} mobile-tab-${selectedMainTab}`}
                    tabIndex={0}
                  >
                    {/* Accessibility BASIC */}
                    {selectedMainTab === "accessibility" &&
                      (venueDetails.templateType === "Basic" || venueDetails.templateType === "") && (
                        <AccessibilitySection
                          venueDetailsResponse={venueDetailsResponse}
                          fullSize={fullSize}
                          initialSection={_initialSection}
                          onLogin={handleShowLoginModal}
                          onRefresh={onRefresh}
                          cid={tb === "photo" ? undefined : cid}
                          tid={tb === "photo" ? undefined : tid}
                          onShowDetailView={onShowDetailView}
                          onNotificationClick={() => handleNotificationClick('accessibility')}
                          isNotificationEnabled={isAccessibilityInfoNotificationEnabled}
                        />
                      )}

                    {selectedMainTab === "photos" && (
                      <PhotosTab
                        venueDetailsResponse={venueDetailsResponse}
                        fullSize={fullSize}
                        openAllPhotosInline={openAllPhotosInline}
                        top5Photos={top5Photos}
                        cid={cid}
                        tid={tid}
                        // onRefresh={onRefresh}
                        onRefresh={() => { }}
                        onShowPhotosDetailView={onShowPhotosDetailView}
                      />
                    )}

                    {selectedMainTab === "alerts" && (
                      <AlertsTab
                        venueDetailsResponse={venueDetailsResponse}
                        onRefresh={onRefresh}
                        showMapperView={showMapperView}
                        onEdit={showEditNote}
                        onFlagNote={showFlagNote}
                        onNotificationClick={() => handleNotificationClick('liveAlerts')}
                        isNotificationEnabled={isLiveAlertNotificationEnabled}
                      />
                    )}

                    {selectedMainTab === "reviews" && (
                      <ReviewsTab
                        reviews={reviews}
                        fullSize={fullSize}
                        onFlag={(review) => {
                          if (!user || !isLoggedIn) {
                            setActionToPerformAfterLogin("flagReview");
                            setActionToPerformValueAfterLogin(review);
                            handleShowLoginModal();
                            return;
                          }
                          setSelectedReview(review);
                          setIsFlagReviewOpen(true);
                        }}
                        isLoggedIn={isLoggedIn}
                        onShowLoginModal={handleShowLoginModal}
                        venueDetails={venueDetails}
                        currentReviewSort={currentReviewSort}
                        onReviewSort={handleReviewSort}
                        sortByText={sortByText}
                      />
                    )}

                    {selectedMainTab === "about" && (
                      <AboutTab
                        venueDetailsResponse={venueDetailsResponse}
                        onRefresh={onRefresh}
                      />
                    )}

                    {/* Dynamic Tabs */}
                    {selectedMainTab.startsWith("dynamicTab_") &&
                      renderDynamicTabContent(selectedMainTab)}

                    {/* Empty state when no tab content is available */}
                    {!hasAccessibilityData &&
                      !hasPhotos &&
                      !hasAlerts &&
                      !hasReviews &&
                      selectedMainTab === "accessibility" && (
                        <div className="flex flex-col items-center justify-center gap-2 max-w-[312px] text-center mx-auto">
                          <h2
                            id="tab-content-title"
                            className="label-m-semibold text-fg-neutral-strong"
                          >
                            {i18n.get("wantMoreAccessibilityDetails")}
                          </h2>
                          <p className="body-s-regular text-fg-neutral-subtle">
                            {i18n.get("clickToBeNotifiedWhenThisVenueHas")}
                          </p>
                          <button
                            className="flex items-center justify-center gap-2 w-full mt-2 px-4 py-2 border border-border-token-brand-minimal rounded-lg label-m-semibold text-fg-neutral-strong hover:bg-bg-brand-ghost-hover transition-colors min-h-[44px]"
                            aria-label="Get notifications for accessibility updates"
                            onClick={() => handleNotificationClick()}
                          >
                            <svg
                              className="w-4 h-4"
                              viewBox="0 0 16 16"
                              fill="currentColor"
                              aria-hidden="true"
                            >
                              <path d="M8 14.5c.83 0 1.5-.67 1.5-1.5h-3c0 .83.67 1.5 1.5 1.5zm5-4V7c0-2.48-1.51-4.5-4-5.04V1.5c0-.55-.45-1-1-1s-1 .45-1 1v.46C4.51 2.5 3 4.52 3 7v3.5L1.5 12v.5h13V12L13 10.5z" />
                            </svg>
                            {i18n.get("iWantToKnow")}
                          </button>
                        </div>
                      )}
                  </section>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Saved Places Modal */}
      {openSavedPlacesModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={openSavedPlacesModal}
          onClose={() => handleSavedPlacesModalClose()}
          aria-label="Save place to list"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          {savePlaceListMode === "create" && (
            <CreateList
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              onCreateList={(listNameCreated) =>
                handleCreateList(listNameCreated)
              }
            />
          )}

          {savePlaceListMode === "list" && (
            <AddRemoveVenueToList
              onClickCreateNew={() => handleCreateNewClick()}
              onClose={() => handleSavedPlacesModalClose()}
              businessId={venueDetails?.business.id}
              venueDetails={venueDetails}
              listNameCreated={listNameCreated}
            />
          )}
        </Modal>
      )}

      {/* Flag Review Modal */}
      <FlagVenueReview
        review={selectedReview}
        venueName={venueDetails.name}
        isOpen={isFlagReviewOpen}
        onClose={() => handleFlagVenueReviewClose()}
        venueDetails={venueDetails}
      />

      {/* Edit Venue Note Modal */}
      {editVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={editVenueNoteOpenModal}
          onClose={() => setEditVenueNoteOpenModal(false)}
          closeOnOverlayClick={false}
          aria-label="Edit venue note"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <EditVenueNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => setEditVenueNoteOpenModal(false)}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
            note={editNote}
          />
        </Modal>
      )}

      {/* Flag Venue Note Modal */}
      {flagVenueNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={flagVenueNoteOpenModal}
          onClose={() => setFlagVenueNoteOpenModal(false)}
          aria-label="Report venue note"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <FlagVenueNote
            venueDetails={venueDetails}
            onClose={() => setFlagVenueNoteOpenModal(false)}
            note={flagNote}
          />
        </Modal>
      )}

      {/* Add Mapper Note Modal */}
      {addNoteOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={addNoteOpenModal}
          onClose={() => handleAddNoteModalClose()}
          aria-label="Add mapper note"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AddMapperNote
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => handleAddNoteModalClose()}
            onInfoClick={() => {
              setAreaDetailsOpenModal(true);
            }}
          />
        </Modal>
      )}

      {/* Area Details Notes Modal */}
      {areaDetailsOpenModal && (
        <Modal
          focusTrapped
          center
          blockScroll={true}
          animationDuration={0}
          open={areaDetailsOpenModal}
          onClose={() => setAreaDetailsOpenModal(false)}
          aria-label="Area details notes"
          classNames={{
            modal:
              "bg-[transparent] p-0 focus:outline-0 shadow-none md:w-[480px]",
            closeButton: "hidden",
          }}
        >
          <AreaDetailsNotes
            venueDetailsResponse={venueDetailsResponse}
            onClose={() => {
              setAreaDetailsOpenModal(false);
            }}
            mode="mappernote"
          />
        </Modal>
      )}

      {/* Interest Notification Modal */}
      <InterestNotificationModal
        businessId={venueDetails.business.id.toString()}
        businessName={venueDetails.name}
        interestId={currentUserInterest?.id?.toString()}
        interestedType={currentUserInterest?.interested_type}
        notifyAccessibilityInformation={
          preSelectedNotificationType === 'accessibility' 
            ? true 
            : (currentUserInterest?.notify_accessibility_information === 1)
        }
        notifyLiveAlert={
          preSelectedNotificationType === 'liveAlerts'
            ? true
            : (currentUserInterest?.notify_live_alert === 1)
        }
        isOpen={isNotificationModalOpen}
        onClose={handleNotificationModalClose}
        onSuccess={handleNotificationModalSuccess}
        onComplete={handleNotificationModalComplete}
        skipSuccessModal={!!preSelectedNotificationType}
        // skipSuccessModal={false}
      />
    </>
  );
}

export default VenueSheetContent;
